import React, { useState } from 'react';
import { Label, Radio, Textarea } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../redux/hooks/useAppDispatch';
import { clearValidationErrors } from '../../../redux/slices/sessionSlice';
import { SessionAssignTo } from '../../../common/enums/SessionAssignTo';

interface SessionUserAssignmentProps {
  assignTo: SessionAssignTo;
  userIds: string[];
  onAssignToChange: (assignTo: SessionAssignTo) => void;
  onUserIdsChange: (userIds: string[]) => void;
}

export const SessionUserAssignment: React.FC<SessionUserAssignmentProps> = ({
  assignTo,
  userIds,
  onAssignToChange,
  onUserIdsChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Local state for raw textarea input to allow proper Enter key functionality
  const [rawUserIdsInput, setRawUserIdsInput] = useState<string>(() =>
    userIds.join('\n')
  );

  // Helper functions for line-separated user IDs
  const stringToUserIds = (str: string): string[] =>
    str
      .split('\n')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

  const handleAssignToAll = () => {
    onAssignToChange(SessionAssignTo.All);
    onUserIdsChange([]);
    setRawUserIdsInput('');
    dispatch(clearValidationErrors('userIds'));
  };

  const handleAssignToSpecific = () => {
    onAssignToChange(SessionAssignTo.Specific);
  };

  const handleUserIdsChange = (inputValue: string) => {
    setRawUserIdsInput(inputValue);
    const parsedIds = stringToUserIds(inputValue);
    onUserIdsChange(parsedIds);
  };

  return (
    <>
      <div>
        <div className="mb-2 block">
          <Label htmlFor="assignTo" value={t('sessions.assignTo')} />
          <span className="ms-1 text-red-500">*</span>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <Radio
              id="assignToAll"
              name="assignTo"
              value={SessionAssignTo.All}
              checked={assignTo === SessionAssignTo.All}
              onChange={handleAssignToAll}
            />
            <Label htmlFor="assignToAll">{t('sessions.allUsers')}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Radio
              id="assignToSpecific"
              name="assignTo"
              value={SessionAssignTo.Specific}
              checked={assignTo === SessionAssignTo.Specific}
              onChange={handleAssignToSpecific}
            />
            <Label htmlFor="assignToSpecific">
              {t('sessions.specificUsers')}
            </Label>
          </div>
        </div>
      </div>

      {/* User Selection - Only show when Specific is selected */}
      {assignTo === SessionAssignTo.Specific && (
        <div>
          <div className="mb-2 block">
            <Label htmlFor="userIds" value={t('sessions.enterUserIds')} />
            <span className="ms-1 text-red-500">*</span>
          </div>
          <Textarea
            id="userIds"
            placeholder={t('sessions.userIdsPlaceholder')}
            value={rawUserIdsInput}
            onChange={(e) => handleUserIdsChange(e.target.value)}
            helperText={t('sessions.userIdsHelper')}
            rows={4}
          />
          {rawUserIdsInput.trim() && (
            <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('sessions.usersSelected', {
                count: stringToUserIds(rawUserIdsInput).length,
              })}
            </p>
          )}
        </div>
      )}
    </>
  );
};
